import React, { PropsWithChildren, ReactNode, useState } from 'react';
import { Box, PaletteColor, SxProps, useTheme } from '@mui/material';
import { AccordionHeader } from '../../molecules/AccordionHeader';
import { AccordionFooter } from '../../molecules';

export type AccordionProps = {
    title: ReactNode;
    subtitle?: string;
    expandLabel?: ReactNode;
    footerActions?: ReactNode[];
    expanded: boolean;
    expandColor?: PaletteColor;
    sx?: SxProps;
    headerSx?: SxProps;
    titlesSx?: SxProps;
    contentSxProps?: SxProps;
    checkBox?: boolean;
    checked?: boolean;
    checkBoxDisabled?: boolean;
    onChange?: (checked: boolean) => void;
    tooltipDisabled?: string;
    tooltipSelected?: string;
    tooltipSelect?: string;
};

export const Accordion: React.FunctionComponent<PropsWithChildren<AccordionProps>> = function (
    props,
) {
    const { palette } = useTheme();
    const {
        title,
        subtitle,
        expandLabel,
        expandColor,
        footerActions = [],
        expanded,
        children,
        sx,
        headerSx,
        titlesSx,
        contentSxProps,
        checkBox = false,
        checked = false,
        checkBoxDisabled = false,
        tooltipDisabled,
        tooltipSelected,
        tooltipSelect,
        onChange,
    } = props;
    const [toggle, setToggle] = useState<boolean>(expanded);

    const wrapperSx = {
        width: '100%',
        borderRadius: '12px',
        borderColor: palette.grey[200],
        borderWidth: '1px',
        borderStyle: 'solid',
        boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
        ...sx,
    };

    const bodySx = {
        display: toggle ? 'block' : 'none',
        overflow: 'hidden',
    };

    const contentSx = {
        padding: '16px',
        ...contentSxProps,
    };

    return (
        <Box
            sx={wrapperSx}
            data-testid="accordion"
        >
            <AccordionHeader
                title={title}
                subtitle={subtitle}
                expandLabel={expandLabel}
                expandColor={expandColor}
                expanded={toggle}
                onToggle={() => setToggle((v) => !v)}
                sx={headerSx}
                titlesSx={titlesSx}
                checkBox={checkBox}
                checked={checked}
                checkBoxDisabled={checkBoxDisabled}
                onChange={onChange}
                tooltipDisabled={tooltipDisabled}
                tooltipSelected={tooltipSelected}
                tooltipSelect={tooltipSelect}
            />
            <Box
                sx={bodySx}
                data-testid="accordion-body"
            >
                <Box sx={contentSx}>{children}</Box>
                {footerActions.length > 0 && <AccordionFooter actions={footerActions} />}
            </Box>
        </Box>
    );
};
